import React from "react";
import { motion } from "framer-motion";
import { SiteLink } from "./SiteLink";

const journeyNodes = ["Tool 1", "Tool 2", "Tool 3", "Tool 4"];

export function Hero() {
  const [activeNode, setActiveNode] = React.useState(0);

  React.useEffect(() => {
    const id = setInterval(() => {
      setActiveNode((prev) => (prev + 1) % journeyNodes.length);
    }, 1400);
    return () => clearInterval(id);
  }, []);

  return (
    <section className="relative overflow-hidden border-b border-zinc-100 dark:border-zinc-800 bg-gradient-to-b from-white via-white to-zinc-50 dark:from-black dark:via-black dark:to-zinc-950">
      {/* Breathing ambient background */}
      <motion.div
        className="pointer-events-none absolute inset-x-0 top-[-22%] flex justify-center opacity-70"
        animate={{ scale: [1, 1.05, 1] }}
        transition={{ duration: 26, repeat: Infinity, ease: "easeInOut" }}
      >
        <div className="h-72 w-[42rem] rounded-[999px] bg-[radial-gradient(circle_at_top,_#fb7185,_#a855f7,_#0ea5e9)] blur-3xl" />
      </motion.div>
      <div className="relative mx-auto flex max-w-5xl flex-col items-center px-6 pt-28 pb-32 text-center sm:pt-32 sm:pb-36 md:pt-36 md:pb-40">
        {/* Badge */}
        {/* <div className="inline-flex items-center gap-2 rounded-full border border-zinc-200 dark:border-zinc-700 bg-white/70 dark:bg-zinc-900/70 px-3 py-1 text-xs font-medium text-zinc-700 dark:text-zinc-300 shadow-sm backdrop-blur">
          <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
          MCP Server Connected
        </div> */}

        {/* Hero headline */}
        <h1 className="mt-12 text-balance text-4xl font-semibold tracking-tight text-zinc-900 dark:text-zinc-100 sm:text-5xl md:text-6xl">
          Transform tool calls into{" "}
          <span className="bg-gradient-to-r from-pink-500 via-orange-500 via-fuchsia-500 to-sky-500 bg-clip-text text-transparent">
            user flows.
          </span>
        </h1>

        {/* Paradigm line (chosen by user) */}
        {/* <p className="mt-5 max-w-2xl text-pretty text-sm font-medium text-zinc-800 dark:text-zinc-200 sm:text-base">
          Agents assemble user experiences.{" "}
          <span className="font-medium">
            Taias lets you influence them.
          </span>
        </p> */}

        {/* Secondary description */}
        <p className="mt-8 max-w-2xl text-pretty text-xs text-zinc-500 dark:text-zinc-400 sm:text-lg font-base">
          Taias is your MCP Server's flow layer informing agents and users the
          best next steps. Agents decide what to do next, but you guide them.
        </p>

        {/* Primary CTA only */}
        <div className="mt-14 flex flex-wrap items-center justify-center gap-4">
          <SiteLink
            href="/docs"
            className="inline-flex items-center justify-center rounded-full bg-black dark:bg-white px-6 py-3 text-sm font-semibold text-white dark:text-black shadow-lg shadow-black/10 dark:shadow-white/10 transition hover:-translate-y-0.5 hover:bg-zinc-900 dark:hover:bg-zinc-100"
          >
            Build your first flow
          </SiteLink>
        </div>

        {/* Abstract transformation card */}
        <motion.div
          className="mt-20 w-full max-w-4xl sm:mt-24 md:mt-28"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: "easeOut" }}
        >
          <motion.div className="relative rounded-3xl border border-zinc-100 dark:border-zinc-800 bg-white/80 dark:bg-zinc-900/80 p-6 shadow-[0_24px_80px_rgba(15,23,42,0.12)] dark:shadow-[0_24px_80px_rgba(0,0,0,0.4)] backdrop-blur-sm sm:p-8">
            <div className="pointer-events-none absolute inset-0 -z-10 rounded-3xl bg-gradient-to-br from-zinc-50 via-white to-sky-50 dark:from-zinc-900 dark:via-black dark:to-sky-950/20" />
            <div className="grid gap-8 text-left lg:grid-cols-2 lg:items-center">
              {/* BEFORE: abstract chaos */}
              <div className="space-y-4">
                <p className="text-xs font-semibold uppercase tracking-[0.16em] text-zinc-500 dark:text-zinc-400">
                  Before — fragmented agent execution
                </p>
                <motion.div
                  className="relative h-40 overflow-hidden rounded-2xl border border-zinc-100 dark:border-zinc-700 bg-zinc-950/95"
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, ease: "easeOut" }}
                >
                  {/* scattered nodes */}
                  {Array.from({ length: 9 }).map((_, i) => (
                    <motion.div
                      key={i}
                      className="absolute h-3 w-3 rounded-full bg-zinc-800"
                      style={{
                        top: `${10 + ((i * 9) % 70)}%`,
                        left: `${8 + ((i * 13) % 80)}%`,
                      }}
                      animate={{
                        scale: [1, 1.4, 0.9, 1.1, 1],
                        opacity: [0.4, 0.9, 0.6, 0.8, 0.5],
                        x: [0, 4, -3, 2, 0],
                        y: [0, -3, 4, -2, 0],
                      }}
                      transition={{
                        duration: 4 + (i % 3),
                        repeat: Infinity,
                        repeatType: "mirror",
                        ease: "easeInOut",
                        delay: i * 0.12,
                      }}
                    />
                  ))}
                  {/* erratic connecting lines (simple gradients) */}
                  <div className="pointer-events-none absolute inset-0">
                    <div className="absolute left-[12%] top-[20%] h-px w-[55%] bg-gradient-to-r from-fuchsia-500/20 via-zinc-200/40 to-transparent" />
                    <div className="absolute left-[25%] top-[55%] h-px w-[50%] bg-gradient-to-r from-sky-500/30 via-zinc-100/40 to-transparent rotate-[10deg]" />
                    <div className="absolute left-[10%] top-[70%] h-px w-[40%] bg-gradient-to-r from-pink-500/30 via-zinc-100/30 to-transparent -rotate-[8deg]" />
                  </div>
                  {/* subtle label */}
                  <div className="absolute bottom-2 left-3 rounded-full bg-zinc-900/80 px-2.5 py-1 text-[10px] font-medium text-zinc-300">
                    Random · Atomic · Reactive
                  </div>
                </motion.div>
                {/* <p className="text-xs text-zinc-500 dark:text-zinc-400">
                  Tool calls, prompts, and glue code firing in isolation —
                  powerful, but hard to reason about as an experience.
                </p> */}
              </div>

              {/* AFTER: abstract guided journey */}
              <div className="space-y-4">
                <p className="text-xs font-semibold uppercase tracking-[0.16em] text-zinc-500 dark:text-zinc-400">
                  After — guided agent journeys
                </p>
                <motion.div
                  className="relative h-40 overflow-hidden rounded-2xl border border-zinc-100 dark:border-zinc-700 bg-gradient-to-br from-zinc-900 via-zinc-950 to-zinc-900"
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, ease: "easeOut", delay: 0.1 }}
                >
                  {/* path */}
                  <motion.div
                    className="absolute left-[8%] top-1/2 h-px w-[84%] -translate-y-1/2 bg-gradient-to-r from-pink-500 via-fuchsia-500 via-sky-500 to-emerald-400"
                    initial={{ opacity: 0.6 }}
                    animate={{ opacity: [0.5, 0.9, 0.7, 0.9, 0.6] }}
                    transition={{
                      duration: 5,
                      repeat: Infinity,
                      repeatType: "mirror",
                      ease: "easeInOut",
                    }}
                  />
                  {/* journey nodes */}
                  <div className="absolute inset-x-[8%] top-1/2 flex -translate-y-1/2 justify-between">
                    {journeyNodes.map((label, index) => {
                      const isActive = index === activeNode;
                      return (
                        <motion.div
                          key={label}
                          className="flex flex-col items-center gap-1"
                          animate={isActive ? { y: -6 } : { y: 0 }}
                          transition={{
                            type: "spring",
                            stiffness: 180,
                            damping: 20,
                          }}
                        >
                          <motion.div
                            className="flex h-6 w-6 items-center justify-center rounded-full border border-zinc-500/80 bg-zinc-950"
                            animate={
                              isActive
                                ? {
                                    scale: 1.15,
                                    boxShadow: "0 0 18px rgba(236,72,153,0.55)",
                                  }
                                : { scale: 1, boxShadow: "0 0 0 rgba(0,0,0,0)" }
                            }
                            transition={{
                              type: "spring",
                              stiffness: 230,
                              damping: 20,
                            }}
                          >
                            <span className="h-2 w-2 rounded-full bg-gradient-to-br from-pink-500 via-fuchsia-500 to-sky-400" />
                          </motion.div>
                          <span className="text-[10px] font-medium uppercase tracking-[0.14em] text-zinc-300">
                            {label}
                          </span>
                        </motion.div>
                      );
                    })}
                  </div>
                  {/* helper description */}
                  <div className="absolute bottom-2 left-3 rounded-full bg-zinc-900/80 px-2.5 py-1 text-[10px] font-medium text-zinc-200">
                    Flows shape the journey; the agent still drives.
                  </div>
                </motion.div>
                {/* <p className="text-xs text-zinc-500 dark:text-zinc-400">
                  Agent execution happens inside a shaped journey — so the
                  experience stays aligned with your product and business goals.
                </p> */}
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
